import * as React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { PopperProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../../../theme';
import { PassengerState } from '../../../../../Checkout/store/passengers/reducers';
import { Money as MoneyType } from '../../../../../types';
import { useSeatAdditionalInfo } from '../../../SeatsInfo/hooks';
import SeatAdditionalInfo from '../../../SeatsInfo/SeatAdditionalInfo/SeatAdditionalInfo';
import { GdsSeatService, Seat } from '../../../../types';
import SeatPrice from '../../../SeatPrice/SeatPrice';
import { PriceDetailsItem } from '../../types';

interface Props {
	seats: Seat[];
	service: GdsSeatService;
	isAvailable: boolean;
	occupiedBy: PassengerState;
	isSelectedByOtherOrderPassenger?: boolean;
	disabled?: boolean;
	children: React.ReactElement;
	isRecommend?: boolean;
	isFreeSeat?: boolean;
	priceDetails?: PriceDetailsItem[];
	totalSeatPrice?: MoneyType;
	hideSeatName?: boolean;
	isTooltipOpened?: boolean;
	onToggle: (value: boolean) => void;
}

type SeatTooltipPopperProps = PopperProps & {
	'data-rfics'?: string;
};

const SeatTooltip: React.FunctionComponent<Props> = React.memo(
	({
		seats,
		service,
		isAvailable,
		children,
		isRecommend,
		isFreeSeat,
		priceDetails,
		totalSeatPrice,
		hideSeatName,
		isTooltipOpened,
		onToggle
	}) => {
		const theme = useTheme('SeatMap').SeatTooltipStyles;
		const { t } = useTranslation('SeatMap');

		const mainSeat = seats[0];
		const seatNumbers = seats.map(seat => seat.number);

		const { restrictions, permissions, additional, withInfo } = useSeatAdditionalInfo(seats);

		const showPriceDetails = priceDetails?.length > 0;
		const showOnlyTotalPrice = !isFreeSeat && !showPriceDetails;
		const showSeatName = !hideSeatName && service?.name && !showPriceDetails;

		if (!isAvailable || !mainSeat.isAvailable || !mainSeat.isExistent) {
			return children;
		}

		return (
			<Tooltip
				disableInteractive
				slotProps={{
					popper: {
						'data-rfics': mainSeat.rfisc,
						popperOptions: {
							strategy: 'fixed',
							modifiers: [
								{
									name: 'computeStyles',
									options: {
										adaptive: false
									}
								}
							]
						}
					} as SeatTooltipPopperProps
				}}
				classes={{
					tooltip: cn(theme.tooltip, {
						[theme.tooltip_withInfo]: withInfo,
						[theme.tooltip_freeSeat]: isFreeSeat,
						[theme.tooltip_recommend]: isRecommend
					}),
					popper: theme.popper,
					tooltipPlacementTop: theme.tooltip_top
				}}
				open={isTooltipOpened}
				onOpen={() => onToggle(true)}
				onClose={() => onToggle(false)}
				placement="top"
				title={
					<div>
						<div className={theme.container}>
							<div className={theme.number}>{seatNumbers.join(', ')}</div>

							<div className={theme.separator__vertical} />

							<div className={theme.details}>
								{isFreeSeat && <span>{t('Free seat')}</span>}

								{showPriceDetails && (
									<div className={theme.details__priceDetails}>
										{priceDetails.map(({ label, price }) => (
											<div
												key={label}
												className={cn(
													theme.details__priceDetails_item,
													theme.details__priceDetails_price
												)}
											>
												{label}
												<SeatPrice
													moneyClassName={theme.details__priceDetails_price}
													money={price}
												/>
											</div>
										))}

										<div className={theme.separator__horizontal} />

										<div
											key={'total'}
											className={cn(
												theme.details__priceDetails_item,
												theme.details__priceDetails_total
											)}
										>
											{t('Total')}:
											<SeatPrice
												moneyClassName={theme.details__priceDetails_total}
												money={totalSeatPrice}
											/>
										</div>
									</div>
								)}

								{showOnlyTotalPrice && (
									<SeatPrice moneyClassName={theme.details__price} money={totalSeatPrice} />
								)}

								{showSeatName && <span className={theme.details__seatName}>{service?.name}</span>}
							</div>
						</div>

						{withInfo && (
							<SeatAdditionalInfo
								className={theme.info}
								restrictions={restrictions}
								permissions={permissions}
								additional={additional}
							/>
						)}
					</div>
				}
			>
				{children}
			</Tooltip>
		);
	}
);

export default SeatTooltip;
